import { useState, useEffect } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, AlertTriangle, GitMerge } from "lucide-react";

/**
 * Dialog de fusion de clients.
 * Props: { open, onClose, members, onMerged }
 * members = [{ key, client_name, client_email, phone, dominant_city, orders_count, total_ht }]
 */
const MergeDialog = ({ open, onClose, members, onMerged }) => {
  const [primaryKey, setPrimaryKey] = useState("");
  const [mergeFields, setMergeFields] = useState(true);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (open && members.length) {
      const biggest = [...members].sort((a, b) => (b.orders_count || 0) - (a.orders_count || 0))[0];
      setPrimaryKey(biggest?.key || members[0].key);
    }
  }, [open, members]);

  const handleMerge = async () => {
    if (!primaryKey) return;
    const toMerge = members.filter((m) => m.key !== primaryKey).map((m) => m.key);
    if (toMerge.length === 0) {
      toast.error("Sélectionnez au moins un client à fusionner");
      return;
    }
    setMerging(true);
    try {
      const res = await api.mergeClients({
        primary_key: primaryKey,
        to_merge_keys: toMerge,
        merge_fields: mergeFields,
      });
      const d = res.data;
      toast.success(
        `Fusion réussie : ${d.orders_migrated} commande(s) migrée(s), ${d.profiles_deleted} profil(s) supprimé(s)`
      );
      if (onMerged) onMerged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la fusion");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="client-merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary" />
            Fusionner les clients
          </DialogTitle>
          <DialogDescription>
            Choisissez le client principal : les commandes et les informations des autres seront
            migrées sur celui-ci. Les clients fusionnés seront supprimés.
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={primaryKey} onValueChange={setPrimaryKey} className="space-y-2">
          {members.map((m) => (
            <label
              key={m.key}
              htmlFor={`primary-${m.key}`}
              className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                primaryKey === m.key ? "bg-primary/5 border-primary" : "hover:bg-muted/40"
              }`}
              data-testid={`client-merge-option-${m.key}`}
            >
              <RadioGroupItem value={m.key} id={`primary-${m.key}`} className="mt-1" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{m.client_name}</div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {m.client_email && <div>Email : {m.client_email}</div>}
                  {m.phone && <div>Téléphone : {m.phone}</div>}
                  {m.dominant_city && <div>Ville : {m.dominant_city}</div>}
                  <div>
                    {m.orders_count} commande(s) · Total {formatCurrency(m.total_ht || 0)}
                  </div>
                </div>
              </div>
              {primaryKey === m.key && (
                <Badge className="bg-primary text-primary-foreground">Principal</Badge>
              )}
            </label>
          ))}
        </RadioGroup>
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id="merge-fields"
            checked={mergeFields}
            onCheckedChange={(c) => setMergeFields(!!c)}
            data-testid="client-merge-fields-checkbox"
          />
          <Label htmlFor="merge-fields" className="cursor-pointer text-sm">
            Compléter les champs manquants du principal avec ceux des autres (email, téléphone, adresse...)
          </Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={merging}>Annuler</Button>
          <Button onClick={handleMerge} disabled={merging || !primaryKey} data-testid="client-merge-confirm">
            {merging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <GitMerge className="h-4 w-4 mr-2" />
            Fusionner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Liste les groupes de clients doublons (exacts + similaires) et permet de
 * lancer la fusion de chaque groupe.
 */
export const ClientDuplicatesDialog = ({ open, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ exact: [], similar: [] });
  const [mergeTarget, setMergeTarget] = useState(null); // array of members

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.findClientDuplicates();
      setData(res.data || { exact: [], similar: [] });
    } catch {
      toast.error("Erreur lors de la détection des doublons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  const renderGroup = (group, gi, type) => (
    <Card key={`${type}-${gi}`} data-testid={`client-dup-group-${type}-${gi}`}>
      <CardContent className="pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-muted-foreground">
            {group.match_reason}
          </div>
          <Button
            size="sm"
            onClick={() => setMergeTarget(group.members)}
            data-testid={`client-dup-merge-btn-${type}-${gi}`}
          >
            <GitMerge className="h-4 w-4 mr-2" />
            Fusionner
          </Button>
        </div>
        <div className="border rounded-sm">
          <table className="data-table text-sm">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Ville</th>
                <th>Email / Tel</th>
                <th className="text-right">Commandes</th>
                <th className="text-right">Total HT</th>
              </tr>
            </thead>
            <tbody>
              {group.members.map((m) => (
                <tr key={m.key}>
                  <td className="font-medium">{m.client_name}</td>
                  <td className="text-xs">{m.dominant_city || "—"}</td>
                  <td className="text-xs text-muted-foreground">
                    {m.client_email || m.phone || "—"}
                  </td>
                  <td className="text-right font-mono">{m.orders_count}</td>
                  <td className="text-right currency tabular-nums">{formatCurrency(m.total_ht || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const exactCount = (data.exact || []).length;
  const similarCount = (data.similar || []).length;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="client-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Détection de doublons — Clients
            </DialogTitle>
            <DialogDescription>
              Groupes exacts (nom+ville identiques) et similaires (noms proches avec même ville, email ou téléphone).
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : exactCount === 0 && similarCount === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun doublon détecté.
            </div>
          ) : (
            <div className="space-y-6">
              {exactCount > 0 && (
                <div className="space-y-2">
                  <h3 className="font-heading font-medium">Doublons exacts ({exactCount})</h3>
                  <div className="space-y-2">
                    {data.exact.map((g, i) => renderGroup(g, i, "exact"))}
                  </div>
                </div>
              )}
              {similarCount > 0 && (
                <div className="space-y-2">
                  <h3 className="font-heading font-medium">Similaires ({similarCount})</h3>
                  <div className="space-y-2">
                    {data.similar.map((g, i) => renderGroup(g, i, "similar"))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MergeDialog
        open={!!mergeTarget}
        onClose={() => setMergeTarget(null)}
        members={mergeTarget || []}
        onMerged={async () => {
          await fetchData();
          if (onChanged) onChanged();
        }}
      />
    </>
  );
};

export default ClientDuplicatesDialog;
